import { useSessionContext } from "@/contexts/SessionContext";
import { useViewContext } from "@/contexts/ViewContext";
import { useAppointmentActions } from "@/components/schedule/hooks/useAppointmentActions";
import {
  AttachMoney,
  Check,
  Close,
  Description,
  PlayArrow,
  Key,
  WhatsApp,
  Notifications,
  EditCalendar,
} from "@mui/icons-material";
import { Button, Chip, IconButton, Stack, Tooltip } from "@mui/material";
import { VerifyResponsibleDialog } from "./VerifyResponsibleDialog";
import { WarningAppointment } from "@/pages/schedule/components/WarningAppointment";
import { GoogleCalendarButton } from "@/components/common/GoogleCalendarButton";
import { useContractContext } from "@/contexts/ContractContext";
import { PaymentPendingModal } from "../common/PaymentPendingModal";
import { AppointmentReminderModal } from "../common/AppointmentReminderModal";
import { resolveAppointmentReminderWhatsAppContext } from "../common/appointmentReminderWhatsApp";
import { useClinicalRegisterContext } from "@/contexts/ClinicalRegisterContext";
import { useMemo, useState } from "react";
import ConfirmTokenDialog from "./components/ConfirmTokenDialog";
import { SendQuestionnairesTrigger } from "./components/SendQuestionnairesTrigger";
import ScheduleCancelDialog from "./ScheduleCancelDialog";
import ScheduleRescheduleDialog from "./ScheduleRescheduleDialog";
import { currentFeatures, oids } from "@/configs/Settings";
import dayjs from "dayjs";
import { SendWhatsAppMessageDialog } from "src-next/modules/crm-clinical/messaging";
import { resolveOptionalAppointmentBasedOn } from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import type {
  MessagingAppointment,
  MessagingPatient,
  MessagingTemplate,
} from "src-next/modules/crm-clinical/messaging";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { isProntoAtendimentoAppointment } from "./prontoAtendimento/mapProntoAppointmentToQueueItem";
import { isEvolutionAppointment } from "./evolution/isEvolutionAppointment";
import {
  TeleconsultoriaJoinCallButton,
  teleconsultoriaGuestOfAppointment,
  teleconsultoriaGuestRoomUrl,
} from "src-next/modules/teleconsultoria";

const getStatusColor = (status: string) => {
  const colors = {
    cancelled: "error",
    proposed: "default",
    pending: "warning",
    arrived: "success",
    booked: "primary",
  } as const;
  return colors[status as keyof typeof colors] || "default";
};

const getStatusLabel = (status: string) => {
  const labels = {
    cancelled: "Cancelado",
    proposed: "Proposta",
    pending: "Pendente de pagamento",
    arrived: "Confirmado",
    booked: "Confirmado",
    fulfilled: "Finalizado",
  };
  return labels[status as keyof typeof labels] || status;
};

interface ScheduleQueueActionsProps {
  appointment: any;
  setRefetch: any;
  patient?: fhir5.BundleEntry<fhir5.Patient> | undefined;
  handleCancelAppointment: any;
  locationTeleconsulting: boolean;
  locationResource?: fhir5.Location;
  variant?: "compact" | "descriptive"; // Nova prop para controlar o estilo
  /**
   * Templates já filtrados e prontos para o dialog (buscados no ScheduleQueue).
   */
  messagingTemplates: MessagingTemplate[];
  /**
   * Dados do tutor no modo vet (passados por `ScheduleQueue`).
   * - phone: usado para "Abrir no WhatsApp Web"
   * - cpf: usado para envio via FluxZap
   */
  tutorPhone?: string;
  tutorCpf?: string;
}

export function ScheduleQueueActions({
  appointment,
  setRefetch,
  patient,
  handleCancelAppointment,
  locationTeleconsulting,
  locationResource,
  variant = "compact", // Valor padrão é compact para manter compatibilidade
  messagingTemplates,
  tutorPhone,
  tutorCpf,
}: ScheduleQueueActionsProps) {
  const { viewType } = useViewContext();
  const { organizationId } = useOrganizationContext();
  const { user } = useSessionContext();
  const guestRoomUrl = teleconsultoriaGuestRoomUrl(
    appointment?.originalData,
    user?.username ? `Practitioner/${oids.cpf}-${user.username}` : "",
  );
  // Teleconsultor também sem sala: nunca cai no "Iniciar Atendimento" do host.
  const isTeleconsultor =
    Boolean(guestRoomUrl) ||
    (Boolean(user?.username) &&
      teleconsultoriaGuestOfAppointment(appointment?.originalData).practitioner
        ?.reference === `Practitioner/${oids.cpf}-${user.username}`);
  const { inProgress } = useClinicalRegisterContext();

  const { currentContract } = useContractContext();
  const hasSubscriptionFluxPay =
    currentContract?.[0]?.resource?.identifier?.some(
      (coding: any) =>
        coding.system.includes("contract-type") &&
        coding.value === "fluxPayPlan",
    );

  const phone = patient?.resource?.telecom?.find(
    (t) => t.system === "phone",
  )?.value;

  const isVet = !currentFeatures.defaultApp;
  const whatsappPhone = isVet ? tutorPhone : phone;
  const whatsappCpf = isVet ? tutorCpf : undefined;

  const [isSendWhatsAppDialogOpen, setIsSendWhatsAppDialogOpen] =
    useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);

  const reminderWhatsAppContext = useMemo(
    () =>
      resolveAppointmentReminderWhatsAppContext({
        appointment: {
          ...appointment,
          originalData: appointment?.originalData ?? appointment,
        },
        patient,
        locationResource,
        locationTeleconsulting,
        patientPhone: whatsappPhone,
      }),
    [appointment, patient, locationResource, locationTeleconsulting, whatsappPhone],
  );

  const messagingAppointment: MessagingAppointment = useMemo(() => {
    return {
      id: appointment?.id ?? appointment?.originalData?.id ?? "appointment",
      startAtIso:
        appointment?.startTime ??
        appointment?.start ??
        appointment?.originalData?.start ??
        undefined,
    };
  }, [appointment]);

  const messagingPatient: MessagingPatient | null = useMemo(() => {
    if (!patient?.resource?.id) return null;

    const patientNameObj = patient.resource?.name?.find(
      (n: any) => n.use === "usual" || n.use === "official",
    );
    const fullName = String(patientNameObj?.text ?? appointment?.patient ?? "")
      .trim()
      .slice(0, 200);

    if (isVet) {
      return {
        id: `tutor-of-${patient.resource.id}`,
        fullName: fullName ? `Tutor de ${fullName}` : "Tutor",
        cpf: whatsappCpf ? String(whatsappCpf) : undefined,
        phone: whatsappPhone ? String(whatsappPhone) : undefined,
        // Alinha com `useTutorLookup`: tasks/FluxZap usam Patient lógico do tutor (CPF).
        isTutor: true,
      };
    }

    // No modo humano, o envio real depende de CPF (quando o caller não injeta `forReference`).
    const cpfFromId =
      patient.resource.id && String(patient.resource.id).includes("-")
        ? String(patient.resource.id).split("-")[1]
        : "";

    return {
      id: patient.resource.id,
      fullName: fullName || "Paciente",
      cpf: cpfFromId || undefined,
      phone: whatsappPhone ? String(whatsappPhone) : undefined,
    };
  }, [
    appointment?.patient,
    isVet,
    patient?.resource?.id,
    whatsappCpf,
    whatsappPhone,
  ]);

  const {
    startServiceLoading,
    handleRemoveFromCalendar,
    handleStartService,
    handleAddToCalendar,
    loadingCalendar,
    handleViewRecords,
    verifyDialog,
    setVerifyDialog,
    fetchRelated,
    warningAction,
    showPaymentWarning,
    related,
    fetchPayment,
    setShowPaymentWarning,
    paymentModalOpen,
    setPaymentModalOpen,
    loadingSendCharge,
    errorSendCharge,
    loadingConfirmPayment,
    handleSendCharge,
    handleConfirmPayment,
    loadingConfirmProposed,
    handleConfirmProposedAppointment,
  } = useAppointmentActions({ appointment, setRefetch });

  const [isConfirmTokenDialogOpen, setIsConfirmTokenDialogOpen] =
    useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);

  /** Referência FHIR do paciente para tasks / envio de questionários (admin/workflow). */
  const patientReferenceForTasks = useMemo(() => {
    const fromPatientBundle = patient?.resource?.id
      ? `Patient/${patient.resource.id}`
      : null;
    const fromAppointment =
      typeof appointment?.subject?.reference === "string"
        ? appointment.subject.reference
        : null;
    return fromPatientBundle ?? fromAppointment ?? null;
  }, [appointment?.subject?.reference, patient?.resource?.id]);

  const hasActiveEncounterForQuestionnaires = useMemo(
    () =>
      Boolean(
        appointment?.id &&
        inProgress &&
        String(inProgress) === String(appointment.id),
      ),
    [appointment?.id, inProgress],
  );

  const appointmentReferenceForTaskSend = useMemo(
    () =>
      resolveOptionalAppointmentBasedOn(
        appointment?.originalData?.id ?? appointment?.id,
      ),
    [appointment?.id, appointment?.originalData?.id],
  );

  const hasCalendarId = appointment?.originalData?.extension?.some(
    (ext: any) => ext.url.includes("googleCalendarId") && ext.valueString,
  );

  // Verifica se o appointment já passou da data/hora de fim usando dayjs
  const isAppointmentExpired =
    dayjs(appointment.endTime).endOf("day").isBefore(dayjs()) ||
    dayjs(appointment?.originalData?.end)?.endOf("day").isBefore(dayjs());

  const appointmentStatus = appointment?.status ?? "";
  const isBooked = appointmentStatus === "booked";
  const isFulfilled = appointmentStatus === "fulfilled";
  const isPending = appointmentStatus === "pending";
  const isProposed = appointmentStatus === "proposed";

  // Mesmas regras da fila dedicada de PA: sem reagendar; cancelar sempre (exceto cancelled/fulfilled)
  const isProntoAtendimento = isProntoAtendimentoAppointment(
    appointment?.originalData ?? appointment,
  );
  const isEvolution = isEvolutionAppointment(
    appointment?.originalData ?? appointment,
  );
  const canMutateAppointment =
    appointmentStatus !== "cancelled" && appointmentStatus !== "fulfilled";
  /** PA e evolução nunca reagendam; agenda normal mantém reagendar. */
  const showReschedule =
    canMutateAppointment && !isProntoAtendimento && !isEvolution;
  /** Cancelar disponível em pending/proposed/booked etc. (PA e agenda). */
  const showCancel = canMutateAppointment;
  /** Proposed → remarcação: oculto no PA e na evolução. */
  const showConfirmProposed =
    isProposed && !isProntoAtendimento && !isEvolution;

  /** Ícones da barra superior — somente em booked. */
  const showToolbarIconActions = isBooked;
  /** Enviar WhatsApp — próximas (booked) e realizadas (fulfilled). */
  const showWhatsAppSend = isBooked || isFulfilled;
  /** Envio manual de questionários (app): confirmadas ou finalizadas. */
  const showQuestionnaireSend =
    viewType === "user" && (isBooked || isFulfilled);
  /** Cobrança — somente em pending. */
  const showPaymentButton = isPending;
  /** Lembrete — somente em booked. */
  const showReminderButton = showToolbarIconActions;
  /** Ver registros — somente em booked. */
  const showViewRecords = isBooked;
  /** Iniciar: somente booked (já era a regra; PA segue igual). */
  const showStartService = isBooked && !isAppointmentExpired;

  return (
    <>
      <Stack spacing={1} alignItems="flex-end">
        {variant === "descriptive" ? (
          // Layout para modal - todos os botões em uma linha
          <Stack
            direction="column"
            spacing={2}
            sx={{
              width: "100%",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{
                flexWrap: "wrap",
                gap: 1,
                width: "100%",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: "wrap", gap: 1 }}
              >
                {viewType === "user" && user?.refreshTokenGoogle && showToolbarIconActions && (
                  <GoogleCalendarButton
                    isInCalendar={!!hasCalendarId}
                    onAddToCalendar={() => {
                      handleAddToCalendar?.(appointment);
                    }}
                    onRemoveFromCalendar={() => {
                      handleRemoveFromCalendar(appointment);
                    }}
                    disabled={loadingCalendar}
                    loading={loadingCalendar}
                  />
                )}

                {showPaymentButton && (
                  <Tooltip title="Enviar cobrança">
                    <IconButton
                      sx={{ color: "primary800.main" }}
                      onClick={() => {
                        setPaymentModalOpen(true);
                      }}
                    >
                      <AttachMoney />
                      {appointment?.originalData?.extension?.find(
                        (ext: any) =>
                          ext.url ===
                            "https://fluxmed.com.br/fhir/StructureDefinition/notification-sent" &&
                          ext.valueBoolean,
                      ) && (
                        <Check
                          sx={{ fontSize: 16, color: "success700.main" }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                )}

                {whatsappPhone && showWhatsAppSend && (
                  <Tooltip title="Enviar WhatsApp">
                    <IconButton
                      sx={{ color: "success.main" }}
                      onClick={() => setIsSendWhatsAppDialogOpen(true)}
                    >
                      <WhatsApp fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {whatsappPhone && showReminderButton && (
                  <Tooltip title="Enviar lembrete de consulta">
                    <IconButton
                      color="secondary"
                      onClick={() => setReminderModalOpen(true)}
                    >
                      <Notifications fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {showQuestionnaireSend && (
                  <SendQuestionnairesTrigger
                    patientReference={patientReferenceForTasks}
                    variant="descriptive"
                    appointmentStatus={appointment?.status}
                    appointmentReference={appointmentReferenceForTaskSend}
                    hasActiveEncounter={hasActiveEncounterForQuestionnaires}
                    patientName={reminderWhatsAppContext.patientName}
                    patientPhone={whatsappPhone}
                  />
                )}

                {/* Token para Plano de Saúde */}
                {(appointment?.supportingInfo ||
                  appointment?.supportingInformation) &&
                  appointmentStatus !== "booked" &&
                  !isPending &&
                  !isProposed && (
                    <Tooltip title="Gerar token para plano de saúde">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsConfirmTokenDialogOpen(true);
                        }}
                        sx={{ color: "info.main" }}
                      >
                        <Key fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                <Chip
                  label={getStatusLabel(appointment.status)}
                  color={getStatusColor(appointment.status)}
                  sx={{ minWidth: 90 }}
                />
              </Stack>

              {showCancel && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ flexWrap: "wrap", gap: 1 }}
                  >
                    {showReschedule && (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<EditCalendar />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setRescheduleDialogOpen(true);
                        }}
                      >
                        Reagendar Consulta
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Close />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelAppointment(appointment?.originalData);
                      }}
                    >
                      Cancelar
                    </Button>
                  </Stack>
                )}
            </Stack>

            {/* Botão de confirmação para appointments "proposed" - abre remarcação (não no PA) */}
            {showConfirmProposed && (
              <Button
                variant="contained"
                color="success"
                startIcon={<EditCalendar />}
                onClick={async (e) => {
                  e.stopPropagation();
                  setRescheduleDialogOpen(true);
                }}
              >
                Confirmar e Definir Horário
              </Button>
            )}

            {/* Botões principais com texto */}
            {viewType === "user" && (showStartService || showViewRecords) && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", gap: 1, width: "100%" }}
              >
                {showStartService && !isTeleconsultor && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrow />}
                    onClick={async (e) => {
                      e.stopPropagation();
                      handleStartService(appointment);
                    }}
                    disabled={startServiceLoading}
                    fullWidth
                  >
                    Iniciar Atendimento
                  </Button>
                )}
                {isTeleconsultor && (
                  <TeleconsultoriaJoinCallButton
                    roomUrl={guestRoomUrl || ""}
                    appointmentId={appointment?.originalData?.id}
                    patientName={appointment?.patient}
                    // Teleconsultor inicia o próprio atendimento (Encounter com partOf).
                    appointment={appointment?.originalData}
                  />
                )}

                {showViewRecords && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Description />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewRecords(appointment?.originalData);
                    }}
                    fullWidth
                  >
                    Ver Registros
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        ) : (
          // Layout original para lista - dois Stacks separados
          <>
            <Stack direction="row" spacing={2} alignItems="center">
              {viewType === "user" && user?.refreshTokenGoogle && showToolbarIconActions && (
                <GoogleCalendarButton
                  isInCalendar={!!hasCalendarId}
                  onAddToCalendar={() => {
                    handleAddToCalendar?.(appointment);
                  }}
                  onRemoveFromCalendar={() => {
                    handleRemoveFromCalendar(appointment);
                  }}
                  disabled={loadingCalendar}
                  loading={loadingCalendar}
                />
              )}

              {showPaymentButton && (
                <Tooltip title="Enviar cobrança">
                  <IconButton
                    sx={{ color: "primary800.main" }}
                    onClick={() => {
                      setPaymentModalOpen(true);
                    }}
                  >
                    <AttachMoney />
                    {appointment?.originalData?.extension?.find(
                      (ext: any) =>
                        ext.url ===
                          "https://fluxmed.com.br/fhir/StructureDefinition/notification-sent" &&
                        ext.valueBoolean,
                    ) && (
                      <Check sx={{ fontSize: 16, color: "success700.main" }} />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              {whatsappPhone && showWhatsAppSend && (
                <Tooltip title="Enviar WhatsApp">
                  <IconButton
                    sx={{ color: "success.main" }}
                    onClick={() => setIsSendWhatsAppDialogOpen(true)}
                  >
                    <WhatsApp fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {whatsappPhone && showReminderButton && (
                <Tooltip title="Enviar lembrete de consulta">
                  <IconButton
                    color="secondary"
                    onClick={() => setReminderModalOpen(true)}
                  >
                    <Notifications fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {showQuestionnaireSend && (
                <SendQuestionnairesTrigger
                  patientReference={patientReferenceForTasks}
                  variant="compact"
                  appointmentStatus={appointment?.status}
                  appointmentReference={appointmentReferenceForTaskSend}
                  hasActiveEncounter={hasActiveEncounterForQuestionnaires}
                  patientName={reminderWhatsAppContext.patientName}
                  patientPhone={whatsappPhone}
                />
              )}

              {/* Token para Plano de Saúde */}
              {(appointment?.supportingInfo ||
                appointment?.supportingInformation) &&
                appointmentStatus !== "booked" &&
                !isPending &&
                !isProposed && (
                  <Tooltip title="Gerar token para plano de saúde">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsConfirmTokenDialogOpen(true);
                      }}
                      sx={{ color: "info.main" }}
                    >
                      <Key fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

              <Chip
                label={getStatusLabel(appointment.status)}
                color={getStatusColor(appointment.status)}
                sx={{ minWidth: 90 }}
              />
            </Stack>

            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="flex-end"
              sx={{
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              {/* Botão de confirmação para appointments "proposed" - abre remarcação (não no PA) */}
              {showConfirmProposed && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<EditCalendar />}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setRescheduleDialogOpen(true);
                  }}
                >
                  Confirmar e Definir Horário
                </Button>
              )}

              {/* Botões principais com texto */}
              {viewType === "user" && (
                <>
                  {showStartService && !isTeleconsultor && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PlayArrow />}
                      onClick={async (e) => {
                        e.stopPropagation();
                        handleStartService(appointment);
                      }}
                      disabled={startServiceLoading}
                    >
                      Iniciar Atendimento
                    </Button>
                  )}
                  {isTeleconsultor && (
                    <TeleconsultoriaJoinCallButton
                      roomUrl={guestRoomUrl || ""}
                      appointmentId={appointment?.originalData?.id}
                      patientName={appointment?.patient}
                      // Teleconsultor inicia o próprio atendimento (Encounter com partOf).
                      appointment={appointment?.originalData}
                    />
                  )}

                  {showViewRecords && (
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<Description />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewRecords(appointment?.originalData);
                      }}
                    >
                      Ver Registros
                    </Button>
                  )}
                </>
              )}

              {showCancel && (
                  <>
                    {showReschedule && (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<EditCalendar />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setRescheduleDialogOpen(true);
                        }}
                      >
                        Reagendar Consulta
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Close />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelAppointment(appointment?.originalData);
                      }}
                    >
                      Cancelar
                    </Button>
                  </>
                )}
            </Stack>
          </>
        )}
      </Stack>

      {paymentModalOpen && (
        <PaymentPendingModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          loadingSendCharge={loadingSendCharge}
          errorSendCharge={errorSendCharge}
          loadingConfirmPayment={loadingConfirmPayment}
          onSendCharge={handleSendCharge}
          onConfirmPayment={handleConfirmPayment}
          appointment={appointment?.originalData ?? appointment}
          notificationSent={
            !!appointment?.extension?.find(
              (ext: any) =>
                ext.url ===
                  "https://fluxmed.com.br/fhir/StructureDefinition/notification-sent" &&
                ext.valueBoolean,
            )
          }
          whatsappNoticeContext={{
            practitionerName: reminderWhatsAppContext.practitionerName,
            appointmentStart: reminderWhatsAppContext.appointmentStart,
            locationLabel: reminderWhatsAppContext.locationLabel,
            patientName: reminderWhatsAppContext.patientName,
            patientPhone: reminderWhatsAppContext.patientPhone,
          }}
        />
      )}

      {reminderModalOpen && (
        <AppointmentReminderModal
          isOpen={reminderModalOpen}
          onClose={() => setReminderModalOpen(false)}
          reminderContext={reminderWhatsAppContext}
        />
      )}

      {showPaymentWarning && (
        <WarningAppointment
          isOpen={showPaymentWarning}
          handleClose={() => setShowPaymentWarning(false)}
          onConfirm={() => {
            setShowPaymentWarning(false);

            if (warningAction === "navigate") {
              handleViewRecords(appointment?.originalData);
            } else if (warningAction === "startService") {
              handleStartService(appointment, true);
            }
          }}
          appointmentDescription="Esta consulta"
          appointment={appointment?.originalData}
          hasSubscriptionFluxPay={hasSubscriptionFluxPay}
          onPaymentSent={fetchPayment}
        />
      )}

      {isConfirmTokenDialogOpen && (
        <ConfirmTokenDialog
          onConfirm={handleConfirmPayment}
          isOpen={isConfirmTokenDialogOpen}
          handleClose={() => setIsConfirmTokenDialogOpen(false)}
        />
      )}

      {rescheduleDialogOpen && (
        <ScheduleRescheduleDialog
          isOpen={rescheduleDialogOpen}
          setIsOpen={setRescheduleDialogOpen}
          appointment={appointment?.originalData}
          setRefetch={setRefetch}
          payment={[]} // O diálogo busca o pagamento internamente se necessário
        />
      )}

      {verifyDialog && (
        <VerifyResponsibleDialog
          handleClose={() => setVerifyDialog(false)}
          isOpen={true}
          loading={false}
          setLoading={() => {}}
          dataAppointment={appointment?.originalData}
          related={related}
          fetchRelated={fetchRelated}
          patientId={appointment?.subject?.reference?.split("-")[1]}
        />
      )}

      {messagingPatient && (
        <SendWhatsAppMessageDialog
          open={isSendWhatsAppDialogOpen}
          onClose={() => setIsSendWhatsAppDialogOpen(false)}
          appointment={messagingAppointment}
          patient={messagingPatient}
          templates={messagingTemplates}
          organizationId={organizationId}
          practitionerId={`${oids.cpf}-${user?.username}`}
          taskPatientReference={patientReferenceForTasks}
        />
      )}
    </>
  );
}
